// Detail of one cancelled transaction: original record, original journal(s), reversing journal(s), reason/user/date.
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import api from '@/lib/api';

const n2 = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const SKIP = new Set(['id', 'tenant_id', 'journal_entry_id', 'journal_entry_ids', 'journal_version', 'payment_details', 'created_by']);

function JournalTable({ j, testid }) {
  return (
    <div className="rounded-lg border border-border p-2" data-testid={testid}>
      <p className="text-xs font-semibold">{j.reference} · {String(j.date || '').slice(0, 10)} <span className="text-muted-foreground font-normal">{j.entry_number}</span></p>
      <table className="w-full text-xs mt-1">
        <thead><tr className="text-muted-foreground"><th className="text-left">Account</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
        <tbody>{(j.lines || []).map((l, i) => (
          <tr key={i}><td>{l.account_code} {l.account_name}</td><td className="text-right tabular-nums">{l.debit ? n2(l.debit) : ''}</td><td className="text-right tabular-nums">{l.credit ? n2(l.credit) : ''}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export function CancelledTransactionDialog({ archiveId, onOpenChange }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    if (archiveId) api.get(`/accounting/cancelled-transactions/${archiveId}`).then(({ data }) => setD(data)).catch(() => setD({ error: true }));
  }, [archiveId]);
  const o = d?.original || {};
  return (
    <Dialog open={!!archiveId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="cancelled-detail-dialog">
        <DialogHeader><DialogTitle>Cancelled transaction</DialogTitle></DialogHeader>
        {!d ? <p className="text-sm text-muted-foreground">Loading…</p> : d.error ? <p className="text-sm text-rose-600">Could not load.</p> : (
          <div className="space-y-4 text-sm">
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-3" data-testid="cancelled-detail-meta">
              <p><b>Reason:</b> {d.reason}</p>
              <p><b>Cancelled by:</b> {d.cancelled_by_name} · <b>on</b> {String(d.cancelled_at || '').slice(0, 16).replace('T', ' ')} UTC</p>
            </div>
            <div data-testid="cancelled-detail-original">
              <p className="font-semibold mb-1">Original transaction ({d.collection})</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
                {Object.entries(o).filter(([k, v]) => !SKIP.has(k) && v !== null && v !== '' && typeof v !== 'object').map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-border/50"><span className="text-muted-foreground">{k}</span><span>{String(v)}</span></div>
                ))}
              </div>
            </div>
            <div>
              <p className="font-semibold mb-1">Original journal{(d.original_journals || []).length > 1 ? 's' : ''}</p>
              <div className="space-y-2">{(d.original_journals || []).map((j) => <JournalTable key={j.id} j={j} testid={`cancelled-original-je-${j.id}`} />)}</div>
            </div>
            <div>
              <p className="font-semibold mb-1">Reversing journal{(d.reversal_journals || []).length > 1 ? 's' : ''}</p>
              <div className="space-y-2">{(d.reversal_journals || []).map((j) => <JournalTable key={j.id} j={j} testid={`cancelled-reversal-je-${j.id}`} />)}</div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
